/**
 * ExperimentsTableV2 — A/B experiments list (V2 redesign).
 *
 * Flat list (experiments have no sort order, so no drag-to-reorder
 * unlike Promo Banners). Columns: Name, Status, Platform, Rollout,
 * Variants, Started, Edit.
 *
 * Data source: `state.experiments.experiments` via `fetchAllExperiments`.
 */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchAllExperiments } from '../../../actions/experimentActions';
import { LibTable, StatusPill, IconButton } from '../../redesign';

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

const STATUS_TONE = {
  draft: 'gray',
  running: 'green',
  paused: 'amber',
  completed: 'blue',
  decided: 'navy',
};

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function ExperimentRow({ experiment }) {
  // The winning variant is marked inline in the variants column rather
  // than adding a new column — a decided experiment is rare enough that
  // a dedicated column would sit empty for every other row.
  //
  // Keyed on `winning_variant`, not on the status: archiving a decided
  // experiment to `completed` must not erase the ★ from the list.
  const variantNames = Array.isArray(experiment.variants)
    ? experiment.variants
        .map((v) => (v.name && v.name === experiment.winning_variant ? `${v.name} ★` : v.name))
        .join(', ')
    : '—';

  // A decided experiment is NOT served at its stored rollout: the server
  // rewrites the client config to the winner at weight 100 and rollout
  // 100. Rendering the stored number alone told the operator that a
  // decided experiment was still at (say) 50% while every device was
  // getting the winner — so the effective figure leads and the stored one
  // is labelled as history.
  const isDecided = experiment.status === 'decided';

  return (
    <LibTable.Row>
      <LibTable.Td>
        <Link
          to={`/experiments/edit/${experiment.slug}`}
          className="font-semibold text-navy no-underline hover:text-teal-green-dark block truncate"
          style={{ fontSize: 14, lineHeight: 1.25 }}
          title={experiment.name || experiment.slug}
        >
          {experiment.name || experiment.slug}
        </Link>
        <div className="font-mono text-text-muted truncate" style={{ fontSize: 11, marginTop: 2 }}>
          {experiment.slug}
        </div>
      </LibTable.Td>
      <LibTable.CenterTd>
        <StatusPill tone={STATUS_TONE[experiment.status] || 'gray'}>
          {experiment.status}
        </StatusPill>
      </LibTable.CenterTd>
      <LibTable.CenterTd>{experiment.platform}</LibTable.CenterTd>
      <LibTable.CenterTd>
        {isDecided ? (
          <>
            <div>100%</div>
            <div className="text-text-muted" style={{ fontSize: 10.5, lineHeight: 1.3 }}>
              serving the winner · stored {experiment.rollout_percentage}%
            </div>
          </>
        ) : (
          `${experiment.rollout_percentage}%`
        )}
      </LibTable.CenterTd>
      <LibTable.Td>
        <span className="text-text-muted" style={{ fontSize: 12 }}>{variantNames}</span>
      </LibTable.Td>
      <LibTable.CenterTd>
        <span className="text-text-muted" style={{ fontSize: 12 }}>{formatDate(experiment.started_at)}</span>
      </LibTable.CenterTd>
      <LibTable.Td style={{ textAlign: 'right' }}>
        <div className="inline-flex opacity-0 transition-opacity duration-150 group-hover:opacity-100">
          <Link to={`/experiments/edit/${experiment.slug}`} aria-label={`Edit ${experiment.name || experiment.slug}`}>
            <IconButton size="sm" aria-label="Edit">{EditIconSvg}</IconButton>
          </Link>
        </div>
      </LibTable.Td>
    </LibTable.Row>
  );
}

export default function ExperimentsTableV2() {
  const dispatch = useDispatch();
  const experiments = useSelector((s) => s.experiments?.experiments);

  useEffect(() => {
    dispatch(fetchAllExperiments());
  }, [dispatch]);

  if (!Array.isArray(experiments)) {
    return (
      <div className="flex items-center justify-center text-text-muted" style={{ height: 256 }}>
        Loading experiments…
      </div>
    );
  }

  return (
    <div style={{ padding: '0 28px 32px' }}>
      {experiments.length === 0 ? (
        <div
          className="bg-surface border border-sand text-text-muted italic text-center"
          style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
        >
          No experiments yet — create one to get started.
        </div>
      ) : (
        <LibTable fixed className="border border-sand" style={{ borderRadius: 12, overflow: 'hidden' }}>
          <colgroup>
            <col />
            <col style={{ width: '12%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '8%' }} />
          </colgroup>
          <LibTable.Head>
            <tr>
              <LibTable.Th>Experiment</LibTable.Th>
              <LibTable.CenterTh>Status</LibTable.CenterTh>
              <LibTable.CenterTh>Platform</LibTable.CenterTh>
              <LibTable.CenterTh>Rollout</LibTable.CenterTh>
              <LibTable.Th>Variants</LibTable.Th>
              <LibTable.CenterTh>Started</LibTable.CenterTh>
              <LibTable.Th style={{ textAlign: 'right' }}>Actions</LibTable.Th>
            </tr>
          </LibTable.Head>
          <tbody>
            {experiments.map((exp) => (
              <ExperimentRow key={exp.slug} experiment={exp} />
            ))}
          </tbody>
        </LibTable>
      )}
    </div>
  );
}
